import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuth } from './useAuth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Registers for native push on iOS/Android via Capacitor.
// On web (PWA) this is a no-op — web push is not currently implemented.
export function usePushNotifications() {
  const { getIdToken, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !Capacitor.isNativePlatform()) return;

    const platform = Capacitor.getPlatform() as 'ios' | 'android';

    const setup = async () => {
      const { receive } = await PushNotifications.requestPermissions();
      if (receive !== 'granted') return;
      await PushNotifications.register();
    };

    // APNs / FCM token — send to backend so SNS can reach this device
    const tokenListener = PushNotifications.addListener('registration', async ({ value: token }) => {
      try {
        const idToken = await getIdToken();
        if (!idToken) return;
        await fetch(`${API_BASE}/api/notifications/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ token, platform }),
        });
      } catch (err) {
        console.error('[push] token registration failed:', err);
      }
    });

    const errorListener = PushNotifications.addListener('registrationError', (err) => {
      console.error('[push] registration error:', err);
    });

    // Tapped from notification centre — route to the relevant screen
    const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const link = action.notification.data?.link as string | undefined;
      if (link) window.location.hash = link;
    });

    setup();

    return () => {
      tokenListener.then(l => l.remove());
      errorListener.then(l => l.remove());
      actionListener.then(l => l.remove());
    };
  }, [isAuthenticated]);
}
